import { showNotification } from '@mantine/notifications';
import { pluralize } from '@repo/common/utils';
import { Icon } from '@repo/foundations';
import type { MutationFunction } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { lowerCase } from 'lodash-es';
import { useBackgroundJob2 } from '../../../components/BackgroundJobProgress/BackgroundJob2.hooks';
import { apiClient, getEndpoints } from '../../common';
import type { Namespace } from '../../types';
import { resourceCatalogQueryKeyFactory } from '../resourceCatalog/constants';
import { secodaEntitiesQueryKeyFactory } from './constants';

export type BulkDeleteData = Array<{
	id: string;
}>;

type RequestData = {
	data: BulkDeleteData;
	signal?: AbortSignal;
};

export function getDefaultBulkDeleteFn(
	namespace: Namespace
): MutationFunction<string, RequestData> {
	return async ({ data, signal }) => {
		const baseUrl = getEndpoints(namespace).root();
		const url = `${baseUrl}bulk_delete`;

		const { data: responseData } = await apiClient.post<string>(url, data, {
			signal,
		});
		return responseData;
	};
}

interface UseBulkDeleteSecodaEntitiesArgs {
	name?: string;
}

function useBulkDeleteSecodaEntities({
	name = 'resources',
}: UseBulkDeleteSecodaEntitiesArgs = {}) {
	const { namespace } = secodaEntitiesQueryKeyFactory;
	const mutationFn = getDefaultBulkDeleteFn(namespace);
	const [startJob] = useBackgroundJob2(
		resourceCatalogQueryKeyFactory.all(),
		'Bulk delete completed successfully',
		false
	);

	return useMutation<string, unknown, RequestData>({
		mutationFn,
		onError: (_error) => {
			const error = _error as AxiosError;
			let message = `An error occurred while bulk deleting ${pluralize(lowerCase(name))}: ${error?.message}`;
			if (error.message?.includes('413')) {
				message = `Too many entities selected for bulk deletion. Please select fewer than 100 entities at a time.`;
			}
			showNotification({
				title: `Error bulk deleting ${pluralize(lowerCase(name))}`,
				message,
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		},
		onSuccess: (data) => {
			startJob(data);
		},
	});
}

export default useBulkDeleteSecodaEntities;
